/* ============================================================================
   biinii-color-theme.js — «Which color are you?» adaptive theme (V3).
   Loaded once per page by any section that needs it (browser dedups by URL).

   Contract:
   - First impression = Biinii brand colors (CSS defaults in biinii-core.css).
   - The theme applies ONLY on an active visitor choice (or a stored one).
   - Second click on the active color = back to defaults (toggle).
   - The choice persists across pages/visits (localStorage `biinii-color`)
     so it can follow the visitor down to the product-page color dots.
   - On dark surfaces, accents use the -dark pair: it follows the theme
     except black, which falls back to gold for legibility.

   API (window.BiiniiTheme):
   - COLORS                 color definitions, keyed
   - KEYS                   ordered keys (lid/body order of the 49 combos)
   - current()              active key or null (brand default)
   - apply(key)             apply + persist a theme
   - reset()                back to brand defaults, clears storage
   - toggle(key)            apply, or reset if key is already active
   - comboOfDay()           {index 0-48, no 1-49, lid, body} deterministic/day
   Emits `biinii:theme` on document with {detail:{key|null}} after changes.
   ============================================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined' || window.BiiniiTheme) return;

  var COLORS = {
    white:  { n: 'White',  hex: '#ffffff', deep: '#6c6e78', tint: '#f3f3f1', ink: '#242634' },
    black:  { n: 'Black',  hex: '#1c1c22', deep: '#1c1c22', tint: '#e9e9ea', ink: '#fafafa' },
    orange: { n: 'Orange', hex: '#F99963', deep: '#b04a12', tint: '#fbe9dd', ink: '#3a1c0a' },
    blue:   { n: 'Blue',   hex: '#0078BF', deep: '#005e96', tint: '#e2eef6', ink: '#ffffff' },
    red:    { n: 'Red',    hex: '#DE4343', deep: '#b32424', tint: '#fae5e5', ink: '#ffffff' },
    yellow: { n: 'Yellow', hex: '#F7D959', deep: '#A16207', tint: '#f6f0d2', ink: '#242634' },
    pink:   { n: 'Pink',   hex: '#E8AFCF', deep: '#a44a78', tint: '#f8eaf1', ink: '#431b30' }
  };
  var KEYS = ['white', 'black', 'orange', 'blue', 'red', 'yellow', 'pink'];
  var PROPS = ['--biinii-acc', '--biinii-acc-deep', '--biinii-acc-tint',
               '--biinii-acc-ink', '--biinii-acc-dark', '--biinii-acc-dark-ink',
               '--biinii-acc-title'];
  var root = document.documentElement;
  var activeKey = null;

  function store(key) {
    try {
      if (key) localStorage.setItem('biinii-color', key);
      else localStorage.removeItem('biinii-color');
    } catch (e) { /* private mode: theme still works for the session */ }
  }
  function emit() {
    document.dispatchEvent(new CustomEvent('biinii:theme', { detail: { key: activeKey } }));
  }

  function apply(key) {
    var c = COLORS[key];
    if (!c) return;
    var dark = key === 'black';
    root.style.setProperty('--biinii-acc', c.hex);
    root.style.setProperty('--biinii-acc-deep', c.deep);
    root.style.setProperty('--biinii-acc-title', c.deep);
    root.style.setProperty('--biinii-acc-tint', c.tint);
    root.style.setProperty('--biinii-acc-ink', c.ink);
    root.style.setProperty('--biinii-acc-dark', dark ? '#DDB158' : c.hex);
    root.style.setProperty('--biinii-acc-dark-ink', dark ? '#242634' : c.ink);
    root.classList.add('bd-user-themed');
    activeKey = key;
    store(key);
    emit();
  }

  function reset() {
    PROPS.forEach(function (p) { root.style.removeProperty(p); });
    root.classList.remove('bd-user-themed');
    activeKey = null;
    store(null);
    emit();
  }

  function toggle(key) {
    if (key === activeKey) reset();
    else apply(key);
  }

  function comboOfDay() {
    var now = new Date();
    var start = new Date(now.getFullYear(), 0, 0);
    var idx = Math.floor((now - start) / 864e5) % 49;
    return { index: idx, no: idx + 1, lid: KEYS[idx % 7], body: KEYS[Math.floor(idx / 7)] };
  }

  /* Restore a stored choice on every page (theme follows the visitor) */
  var stored = null;
  try { stored = localStorage.getItem('biinii-color'); } catch (e) {}
  if (stored && COLORS[stored]) apply(stored);

  window.BiiniiTheme = {
    COLORS: COLORS,
    KEYS: KEYS,
    current: function () { return activeKey; },
    apply: apply,
    reset: reset,
    toggle: toggle,
    comboOfDay: comboOfDay
  };
})();
